import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useLoading } from "./LoadingOverlay";

/**
 * WaterRippleImage
 * - Suntory-like water refraction ripple using a heightmap simulation on a canvas.
 * - Desktop vs Mobile tuning built-in.
 *
 * ✅ Mobile scroll fix:
 * - Desktop uses React pointer events (mouse wake + click drop).
 * - Mobile uses native touch listeners with { passive: true } so scrolling never gets blocked.
 */
export default function WaterRippleImage({
  src,
  alt,
  prefersReducedMotion = false,

  forceMobile = null, // true/false/null
  mobileQuery = "(max-width: 900px)",
  sx,
  children,
}) {
  const { isLoading } = useLoading();

  const hostRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const rafRef = useRef(0);
  const roRef = useRef(null);
  const lastMoveRef = useRef(0);

  const [isMobile, setIsMobile] = useState(() => {
    if (forceMobile !== null) return !!forceMobile;
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(mobileQuery).matches;
  });

  useEffect(() => {
    if (forceMobile !== null) {
      setIsMobile(!!forceMobile);
      return;
    }
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(mobileQuery);
    const onChange = () => setIsMobile(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [forceMobile, mobileQuery]);

  const simRef = useRef({
    w: 0,
    h: 0,
    prev: null,
    curr: null,
    next: null,
    srcPixels: null,
    outImageData: null,

    // runtime tuning
    damping: 0.987,
    refraction: 2.8,
    specular: 0.34,
    dropRadius: 5.5,
    dropStrength: 460,
    gradScale: 0.0036,
    wakePower: 0.34,
    clickPower: 1.25,
    simScale: 2.75,
  });

  const setupSim = useCallback(() => {
    const host = hostRef.current;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!host || !img || !canvas) return;

    const rect = host.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));

    const scale = simRef.current.simScale;
    const simW = Math.max(120, Math.round(cssW / scale));
    const simH = Math.max(90, Math.round(cssH / scale));

    canvas.width = simW;
    canvas.height = simH;

    const N = simW * simH;
    const prev = new Float32Array(N);
    const curr = new Float32Array(N);
    const next = new Float32Array(N);

    const off = document.createElement("canvas");
    off.width = simW;
    off.height = simH;
    const offCtx = off.getContext("2d", { willReadFrequently: true });

    offCtx.clearRect(0, 0, simW, simH);
    offCtx.imageSmoothingEnabled = true;
    offCtx.drawImage(img, 0, 0, simW, simH);
    const src = offCtx.getImageData(0, 0, simW, simH);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    const outImageData = ctx.createImageData(simW, simH);

    simRef.current.w = simW;
    simRef.current.h = simH;
    simRef.current.prev = prev;
    simRef.current.curr = curr;
    simRef.current.next = next;
    simRef.current.srcPixels = src.data;
    simRef.current.outImageData = outImageData;
  }, []);

  const disturb = useCallback((x, y, power = 1) => {
    const sim = simRef.current;
    const { w, h, curr } = sim;
    if (!w || !h || !curr) return;

    const ix = Math.max(2, Math.min(w - 3, Math.round(x)));
    const iy = Math.max(2, Math.min(h - 3, Math.round(y)));

    const radius = sim.dropRadius * (0.9 + 0.9 * power);
    const r2 = radius * radius;
    const strength = sim.dropStrength * power;

    for (let yy = Math.floor(iy - radius); yy <= Math.ceil(iy + radius); yy++) {
      if (yy <= 1 || yy >= h - 2) continue;
      const dy = yy - iy;

      for (let xx = Math.floor(ix - radius); xx <= Math.ceil(ix + radius); xx++) {
        if (xx <= 1 || xx >= w - 2) continue;
        const dx = xx - ix;
        const d2 = dx * dx + dy * dy;
        if (d2 > r2) continue;

        const falloff = 1 - d2 / r2;
        curr[yy * w + xx] -= strength * falloff;
      }
    }
  }, []);

  const stepSimAndRender = useCallback(() => {
    const sim = simRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !sim.curr || !sim.prev || !sim.next || !sim.srcPixels) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const { w, h, damping, refraction, specular, gradScale } = sim;

    const prev = sim.prev;
    const curr = sim.curr;
    const next = sim.next;

    for (let y = 1; y < h - 1; y++) {
      const row = y * w;
      for (let x = 1; x < w - 1; x++) {
        const i = row + x;
        const v =
          (curr[i - 1] + curr[i + 1] + curr[i - w] + curr[i + w]) / 2 - prev[i];
        next[i] = v * damping;
      }
    }

    sim.prev = curr;
    sim.curr = next;
    sim.next = prev;

    const src = sim.srcPixels;
    const out = sim.outImageData.data;

    for (let y = 1; y < h - 1; y++) {
      const row = y * w;
      for (let x = 1; x < w - 1; x++) {
        const i = row + x;

        const hL = sim.curr[i - 1];
        const hR = sim.curr[i + 1];
        const hU = sim.curr[i - w];
        const hD = sim.curr[i + w];

        const dx = (hL - hR) * gradScale;
        const dy = (hU - hD) * gradScale;

        const sx = Math.max(0, Math.min(w - 1, Math.round(x + dx * refraction)));
        const sy = Math.max(0, Math.min(h - 1, Math.round(y + dy * refraction)));

        const si = (sy * w + sx) * 4;
        const oi = i * 4;

        const shade = (dx + dy) * 255 * specular;

        let r = src[si] + shade;
        let g = src[si + 1] + shade;
        let b = src[si + 2] + shade;

        if (r < 0) r = 0;
        if (g < 0) g = 0;
        if (b < 0) b = 0;
        if (r > 255) r = 255;
        if (g > 255) g = 255;
        if (b > 255) b = 255;

        out[oi] = r;
        out[oi + 1] = g;
        out[oi + 2] = b;
        out[oi + 3] = 255;
      }
    }

    ctx.putImageData(sim.outImageData, 0, 0);
    rafRef.current = window.requestAnimationFrame(stepSimAndRender);
  }, []);

  // Apply tuning desktop/mobile
  useEffect(() => {
    const s = simRef.current;

    if (isMobile) {
      s.damping = 0.982;
      s.refraction = 2.0;
      s.specular = 0.22;
      s.dropRadius = 4.0;
      s.dropStrength = 300;
      s.gradScale = 0.0026;
      s.wakePower = 0.18;
      s.clickPower = 0.95;
      s.simScale = 2.35;
    } else {
      s.damping = 0.987;
      s.refraction = 2;
      s.specular = 0.34;
      s.dropRadius = 2.5;
      s.dropStrength = 180;
      s.gradScale = 0.0036;
      s.wakePower = 0.28;
      s.clickPower = 0.38;
      s.simScale = 3.75;
    }

    setupSim();
  }, [isMobile, setupSim]);

  useEffect(() => {
    if (prefersReducedMotion || isLoading) {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }

      return undefined;
    }

    const img = imgRef.current;

    if (!img) {
      return undefined;
    }

    const ensure = () => {
      setupSim();

      if (!rafRef.current) {
        rafRef.current =
          window.requestAnimationFrame(stepSimAndRender);
      }
    };

    if (img.complete) {
      ensure();
    } else {
      img.addEventListener("load", ensure);
    }

    const host = hostRef.current;

    if (host && "ResizeObserver" in window) {
      roRef.current = new ResizeObserver(() => {
        setupSim();
      });

      roRef.current.observe(host);
    }

    return () => {
      img.removeEventListener("load", ensure);

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }

      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
    };
  }, [
    isLoading,
    prefersReducedMotion,
    setupSim,
    stepSimAndRender,
  ]);

  // ---------------------------
  // Desktop interaction (mouse)
  // ---------------------------
  const handlePointerMove = (e) => {
    if (prefersReducedMotion) return;
    if (isMobile) return; // ✅ mobile uses native touch listeners instead
    if (e.pointerType !== "mouse") return;

    const now = performance.now ? performance.now() : Date.now();
    if (now - lastMoveRef.current < 18) return;
    lastMoveRef.current = now;

    const host = hostRef.current;
    const sim = simRef.current;
    if (!host || !sim.w || !sim.h) return;

    const rect = host.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (sim.w / rect.width);
    const y = (e.clientY - rect.top) * (sim.h / rect.height);

    disturb(x, y, sim.wakePower);
  };

  const handlePointerDown = (e) => {
    if (prefersReducedMotion) return;
    if (isMobile) return; // ✅ mobile uses native touch listeners instead

    const host = hostRef.current;
    const sim = simRef.current;
    if (!host || !sim.w || !sim.h) return;

    const rect = host.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (sim.w / rect.width);
    const y = (e.clientY - rect.top) * (sim.h / rect.height);

    disturb(x, y, sim.clickPower);
  };

  // ---------------------------
  // Mobile interaction (passive touch)
  // ---------------------------
  useEffect(() => {
    if (prefersReducedMotion) return;
    if (!isMobile) return;

    const host = hostRef.current;
    if (!host) return;

    // Small threshold: if user is scrolling, don't spam ripples
    let startX = 0;
    let startY = 0;
    let moved = false;

    const getSimXYFromTouch = (t) => {
      const sim = simRef.current;
      const rect = host.getBoundingClientRect();
      const x = (t.clientX - rect.left) * (sim.w / rect.width);
      const y = (t.clientY - rect.top) * (sim.h / rect.height);
      return { x, y };
    };

    const onTouchStart = (ev) => {
      if (!ev.touches || ev.touches.length === 0) return;
      const t = ev.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      moved = false;

      // ✅ immediate “drop” on tap start (still scrollable because passive)
      const sim = simRef.current;
      if (!sim.w || !sim.h) return;
      const { x, y } = getSimXYFromTouch(t);
      disturb(x, y, sim.clickPower);
    };

    const onTouchMove = (ev) => {
      if (!ev.touches || ev.touches.length === 0) return;
      const t = ev.touches[0];

      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      // if the user is actually scrolling (vertical move), let it scroll and keep ripples minimal
      if (Math.abs(dy) > 10) {
        moved = true;
        return;
      }

      // if user is “playing” (small moves), add a very light wake (still passive)
      const sim = simRef.current;
      if (!sim.w || !sim.h) return;

      const now = performance.now ? performance.now() : Date.now();
      if (now - lastMoveRef.current < 28) return;
      lastMoveRef.current = now;

      const { x, y } = getSimXYFromTouch(t);
      disturb(x, y, sim.wakePower * 0.55); // keep light on mobile
    };

    const onTouchEnd = () => {
      moved = false;
    };

    // ✅ passive listeners => NEVER block scroll
    host.addEventListener("touchstart", onTouchStart, { passive: true });
    host.addEventListener("touchmove", onTouchMove, { passive: true });
    host.addEventListener("touchend", onTouchEnd, { passive: true });
    host.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      host.removeEventListener("touchstart", onTouchStart);
      host.removeEventListener("touchmove", onTouchMove);
      host.removeEventListener("touchend", onTouchEnd);
      host.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isMobile, prefersReducedMotion, disturb]);

  return (
    <Box
      ref={hostRef}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        // ✅ allow page vertical scrolling + keep gestures smooth
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch",
        ...sx,
      }}
    >
      {/* hidden source image used to sample pixels */}
      <Box
        component="img"
        ref={imgRef}
        src={src}
        alt={alt}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {prefersReducedMotion ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
            imageRendering: "auto",
            pointerEvents: "none", // ✅ critical: canvas never steals touch gestures
          }}
        />
      )}

      {/* overlays */}
      {typeof children === "function" ? children({ isMobile }) : children}
    </Box>
  );
}
